import React, { useCallback, useContext } from 'react';

import { useTranslation } from 'react-i18next';
import { ThemeContext } from 'styled-components';

import { EmojiImage } from '@pkg/components/Elements/EmojiImage';
import { Toast } from '@pkg/components/Toast';
import { useTimeout } from '@pkg/hooks';

import { type NotificationMessage } from '@app/store/types/notification';

import { Typography } from '../../DesignSystem';

import { ContentWrapper, NotificationWrapper } from './styles';

export interface NotificationProps {
    notification: NotificationMessage;
    onTimeout(notification: NotificationMessage): void;
    onClick(notification: NotificationMessage): void;
}

function Notification({ notification, onTimeout, onClick }: NotificationProps) {
    const theme = useContext(ThemeContext);
    const { t: translate } = useTranslation();

    const { duration = 5000, title = '', message = '', severity, variant, emoji } = notification;

    // There is a runtime issue where an object is being passed
    // directly to the notification in the message field.
    // TODO: Track down all notification calls and make sure they are valid
    const messageText = message ?? translate('common:unknown_error');
    const handleTimeout = useCallback(() => onTimeout(notification), [notification, onTimeout]);

    useTimeout(handleTimeout, {
        duration: variant === 'primary' ? duration + 1000 : duration,
        playing: duration > 0,
    });

    const handleClick = () => {
        onClick(notification);
    };

    if (variant && variant === 'primary') {
        return <Toast emoji={emoji} message={messageText} timeout={duration} />;
    }

    return (
        <NotificationWrapper
            style={{ translateX: '-50%' }}
            initial={{ opacity: 0, y: 120 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 120 }}
            transition={{
                duration: theme.transitions.speeds.fast,
                ease: theme.transitions.eases.cubic,
            }}
            onClick={handleClick}
        >
            {severity === 'alert' && <EmojiImage emojiId={'⚠️'} sizeInPixels={25} />}
            <ContentWrapper $centered={!title && (!severity || severity !== 'alert')}>
                {title && (
                    <Typography type="headline" size={20}>
                        {title}
                    </Typography>
                )}
                <Typography type="paragraph" size={18} color={theme.colors.white}>
                    {messageText}
                </Typography>
            </ContentWrapper>
        </NotificationWrapper>
    );
}

export default Notification;
